import { useEffect, useMemo, useRef, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { destroyCookie } from 'nookies';

import { GETAPP_PRICING_OPTIONS } from 'bx-forms/eas/getapp';
import { gtm } from 'components/Analytics/hooks/useGoogleTagManager';
import Category from 'components/Category';
import CategoryInfoCard from 'components/CategoryInfoCard';

// Dynamic import to avoid SSR issues with bx-forms client components
const InlineForm = dynamic(
  () => import('bx-forms/eas/getapp').then((mod) => mod.InlineForm),
  { ssr: false }
);
import Filters from 'components/Filters';
import Form from 'components/Form';
import Grid, { Container } from 'components/Grid';
import Icon from 'components/Icon';
import { Navigation } from 'components/Layout/LayoutV1';
import LazyContent from 'components/LazyContent';
import ListingSkeleton from 'components/ListingSkeleton';
import OneTapScript from 'components/OneTapScript';
import Pagination from 'components/Pagination';
import Paper from 'components/Paper';
import SortSelect from 'components/SortSelect';
import Typography from 'components/Typography';
import { useAppContext } from 'context/AppContext';
import { EventContext, useEventContext } from 'context/EventContext';
import { usePreload } from 'context/PreloadContext';
import { revealSEOUrl } from 'helpers/SEOHelper';
import detachTask from 'helpers/detachTask';
import { pluralize } from 'helpers/formatters';
import { STATUS } from 'helpers/spa';
import useHighlyViewedIntersect from 'helpers/useHighlyViewedIntersect';
import useSerpUgcViewIntersect from 'helpers/useSerpUgcViewIntersect';
import { useOnScreen, useUser } from 'hooks';
import useViewport from 'hooks/useViewport';

import useLasForm from '../../hooks/useLasForm';
import { BuyersGuideProvider } from '../../providers/buyersGuide';
import ListingCard from '../ListingCard';
import PopularComparisons from '../PopularComparisons';

import classes from './SerpDefault.module.scss';

const SortOptionsDialog = dynamic(() => import('../SortOptionsDialog'));
const BuyersGuide = dynamic(() => import('../BuyersGuide'));

const TWENTY_SECONDS = 20 * 1000;
const STICKY_CATEGORY_INFO_HEIGHT = 94;
const Serp = ({
  serpData: {
    category,
    facets,
    listings,
    pagination,
    related_categories,
    sorting_options,
    key_features,
    comparisons,
  },
  buyersGuide,
}) => {
  const { head, header } = useAppContext();
  const [user] = useUser();
  const { is, down, up } = useViewport();
  const { sendEvent } = useEventContext();
  const router = useRouter();
  const { status } = usePreload();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const selectedSortBy =
    sorting_options.find((sort) => sort.selected)?.slug || 'relevance';
  const stickyContainerRef = useRef();
  const listingResultText = `${pagination.num_items} ${pluralize(pagination.num_items, 'software option')}`;
  const [buyersGuideOnScreen, buyersGuideRef] = useOnScreen({
    multipleIntersections: true,
    rootMargin: '0px 0px -50% 0px',
  });
  const isFirstPage = pagination.page === 1;
  const [selectedTab, setSelectedTab] = useState('all-products');
  const [expandFeaturesContent, setExpandFeaturesContent] = useState(false);
  const [isOneTapScriptVisible, setIsOneTapScriptVisible] = useState(false);

  const { experiments } = useAppContext();

  useEffect(() => {
    if (experiments?.variations?.directoryOneTapDesktop) {
      setIsOneTapScriptVisible(true);
    }

    const timer = setTimeout(() => {
      if (experiments?.variations?.directoryOneTapMobile) {
        setIsOneTapScriptVisible(true);
      }

      gtm.sendEvent({ name: 'exp_delay_timer', delay_seconds: 20 });
      gtm.sendEvent({ time_delay_elapsed: true });
    }, TWENTY_SECONDS);

    return () => clearTimeout(timer);
  }, [
    experiments?.variations?.directoryOneTapDesktop,
    experiments?.variations?.directoryOneTapMobile,
  ]);

  // NOTE: remember to disable SPA for experiments
  const spaDisabled = true;
  const isFiltered = useMemo(
    () =>
      Object.entries(facets).some(([key, filters]) =>
        filters.some(
          (filter) =>
            filter.selected &&
            key !== 'rating_stars_ge' &&
            filter.value !== 'all'
        )
      ),
    [facets]
  );

  const hasBuyersGuide = !!buyersGuide && isFirstPage && !isFiltered;

  const tabUrls = useMemo(
    () =>
      buyersGuide && hasBuyersGuide
        ? header.tab_urls
        : header.tab_urls?.filter((x) => x.slug !== 'guide'),
    [buyersGuide, hasBuyersGuide, header.tab_urls]
  );

  useEffect(() => {
    setSelectedTab(buyersGuideOnScreen ? 'guide' : 'all-products');
  }, [buyersGuideOnScreen]);

  useLasForm({
    hasPplExitIntentEnabled: head?.hasPplExitIntentEnabled,
    global_category_id: category.global_category_id,
    isPPL: true,
  });

  // Opener for the PPL price form used by eligible category cards.
  const onShowPricingModal = useLasForm({
    hasPplExitIntentEnabled: false,
    global_category_id: category.global_category_id,
    isPPL: true,
    config: {
      formTemplate: 'price',
    },
    form: 'openLasModal',
    hasCallFunction: true,
  });

  const showPriceCta = !!head?.hasPplDemoFormEnabled;

  const isNoFollowRelatedCategories = !isFirstPage || isFiltered;

  const toggleDialog = () => {
    if (isDialogOpen) {
      detachTask(sendEvent, {
        name: 'engagement_sort',
        engagementComponent: 'sorting-options',
        engagementDetails: 'icon_remove',
        engagementDestination: 'close-sorting-options',
      });
    }
    detachTask(setIsDialogOpen, !isDialogOpen);
  };

  useHighlyViewedIntersect(status, sendEvent);
  useSerpUgcViewIntersect(status, sendEvent);

  const clearCallback = () => {
    destroyCookie(null, `per_${head.category.slug}`, { path: '/' });
  };
  const handleExpandFeaturesContent = () => {
    setExpandFeaturesContent(!expandFeaturesContent);
  };

  return (
    <div className={classes.root}>
      {isOneTapScriptVisible && <OneTapScript user={user} />}
      <CategoryInfoCard
        category={{
          ...category,
          name: head.page_header || category.name,
          key_features: isFirstPage ? key_features : [],
          ...((pagination.page !== 1 || isFiltered) && { definition: null }),
        }}
        hasClickToCall={head.hasClickToCall}
        facets={isFirstPage && facets}
        expandFeaturesContent={expandFeaturesContent}
      />

      <Navigation
        navTabs={tabUrls}
        selectedTab={selectedTab}
        className={classes.navigation}
        onReadMore={handleExpandFeaturesContent}
        expandFeaturesContent={expandFeaturesContent}
        hasFeaturesContent={key_features?.length > 0 && isFirstPage}
      />

      <Container fixedWidth={true} className={classes.pageContentContainer}>
        {isDialogOpen && (
          <SortOptionsDialog
            isDialogOpen={isDialogOpen}
            toggleDialog={toggleDialog}
            sorting_options={sorting_options}
          />
        )}
        <BuyersGuideProvider state={buyersGuide}>
          <Grid
            container={true}
            spacing={2}
            className={classes.relativeResults}
          >
            {up('md') && (
              <Grid item={true} md={4} lg={3}>
                <div ref={stickyContainerRef}>
                  {/* Sticky filters alters the DOM and react can't find the correct parent, adding a div to prevent this
                    issue */}
                  <div>
                    <Filters
                      facets={facets}
                      clearCallback={clearCallback}
                      rootUrl={category.cta_urls.detail}
                      variant="permanent"
                      stickyContainerRef={stickyContainerRef}
                      disableSPA={spaDisabled}
                      topSpacing={STICKY_CATEGORY_INFO_HEIGHT}
                    />
                  </div>
                </div>
              </Grid>
            )}

            <Grid item={true} lg={9} md={8} xs={12} sm={12}>
              {down('sm') && (
                <>
                  <Paper className={classes.sortingPaper}>
                    <Typography variant="heading-md">
                      {listingResultText}
                    </Typography>
                    <hr className={classes.separator} />
                    <div className={classes.smSorting}>
                      <Filters
                        facets={facets}
                        disabled={status === STATUS.loading}
                        clearCallback={clearCallback}
                        rootUrl={category.cta_urls.detail}
                        variant="temporary"
                        stickyFilterPosition={classes.stickyFilterPosition}
                        disableSPA={spaDisabled}
                        drawerSeoFrendly={true}
                      />
                      <div className={classes.sortWithLabel}>
                        <SortSelect
                          testId="sort-by"
                          showAboutSorting={is('xs')}
                          selectedValue={selectedSortBy}
                          onSelect={(label, value) => {
                            if (value === 'dialog') {
                              toggleDialog();
                            } else {
                              const selected = sorting_options.find(
                                (sort) => sort.slug === value
                              );
                              sendEvent({
                                name: 'engagement_sort',
                                engagementComponent: 'sort',
                                engagementDestination: 'sort_by-option',
                                engagementDetails: `sort-item_${label.toLowerCase().replace(/\s/g, '-')}`,
                              });
                              if (spaDisabled)
                                window.location.href = revealSEOUrl(
                                  selected.url
                                );
                              else {
                                router.push(
                                  revealSEOUrl(selected.url),
                                  undefined,
                                  { scroll: false }
                                );
                              }
                            }
                          }}
                          values={sorting_options.map((sort) => ({
                            label: sort.label,
                            value: sort.slug,
                          }))}
                        />
                      </div>
                    </div>
                  </Paper>
                </>
              )}
              {up('md') && (
                <div className={classes.sortingActions}>
                  <Typography
                    className={classes.listingResults}
                    variant="heading-md"
                  >
                    {listingResultText}
                  </Typography>

                  <div className={classes.sortWithLabel}>
                    <Form.Label id="sort-by-label">
                      <span
                        role="button"
                        tabIndex="0"
                        aria-pressed="false"
                        className={classes.sortBy}
                        onClick={toggleDialog}
                      >
                        <span>Sort by</span>{' '}
                        <Icon prefix="far" icon="circle-info" />
                      </span>
                    </Form.Label>

                    <SortSelect
                      testId="sort-by"
                      showAboutSorting={is('xs')}
                      selectedValue={selectedSortBy}
                      onSelect={(label, value) => {
                        if (value === 'dialog') {
                          toggleDialog();
                        } else {
                          const selected = sorting_options.find(
                            (sort) => sort.slug === value
                          );
                          sendEvent({
                            name: 'engagement_sort',
                            engagementComponent: 'sort',
                            engagementDestination: 'sort_by-option',
                            engagementDetails: `sort-item_${label.toLowerCase().replace(/\s/g, '-')}`,
                          });
                          if (spaDisabled)
                            window.location.href = revealSEOUrl(selected.url);
                          else {
                            router.push(revealSEOUrl(selected.url), undefined, {
                              scroll: false,
                            });
                          }
                        }
                      }}
                      values={sorting_options.map((sort) => ({
                        label: sort.label,
                        value: sort.slug,
                      }))}
                    />
                  </div>
                </div>
              )}

              {status === STATUS.idle &&
                listings.map((listing, index) => (
                  <ListingCard
                    key={listing.name}
                    listing={listing}
                    listingPosition={index + 1}
                    onShowPricingModal={onShowPricingModal}
                    showPriceCta={showPriceCta}
                  />
                ))}
              {status === STATUS.loading && (
                <div>
                  {[...Array(listings.length)].map((_, i) => (
                    <ListingSkeleton
                      // eslint-disable-next-line react/no-array-index-key
                      key={`listing-skeleton-${i}`}
                    />
                  ))}
                </div>
              )}
              {status === STATUS.error && <h1>error</h1>}
              {pagination.num_pages > 1 && (
                // FIXME: unify SPA props names
                <Pagination
                  className={classes.pagination}
                  pagination={pagination}
                  spa={!spaDisabled}
                />
              )}
            </Grid>
          </Grid>
          <div style={{ margin: '32px 0', minHeight: 300 }}>
            <InlineForm
              formId="6SLWtXqH3GlxoaFs7U12Nh"
              gatewayUrl="https://www.capterra.com/bx-capterra-api/bx-forms/submit"
              formTemplate="INLINE_CARD"
              qualificationOptions={GETAPP_PRICING_OPTIONS}
              step2QuestionLabel="Which pricing model do you prefer?"
              headline="Top-rated software of 2026"
              subHeading="Fill out the form and we'll send a list of the top-ranked software based on real user reviews directly to your inbox."
              buttonText="SEND ME THE LIST"
              successHeadline="Thank you, enjoy!"
              successMessage="Your list will be delivered to your inbox shortly."
              step2Subtitle="Thank you! Let us know which pricing model you prefer and we'll send a list of tools with the right pricing available."
              contextProps={{ pageType: 'directory', pagePlacement: 'inline' }}
              tracking={{
                formName: 'DMBUY_2022_GA_Blog_Acq',
                contentCategory: 'Directory_Inline',
                contentRequest: 'Top Software',
              }}
            />
          </div>
          {!!comparisons?.length && (
            <section>
              <Typography component="h3" className={classes.title}>
                {`Popular ${category.name.toLowerCase()} comparisons`}
              </Typography>
              <PopularComparisons comparisons={comparisons} />
            </section>
          )}
          {hasBuyersGuide && (
            <div
              className={classes.buyersGuideContainer}
              data-testid="buyers-guide"
              ref={buyersGuideRef}
            >
              <span
                className={classes.anchor}
                id="guide"
                style={{ height: STICKY_CATEGORY_INFO_HEIGHT }}
              />
              <LazyContent minHeight={1000} rootMargin="1000px">
                <BuyersGuide data={buyersGuide} />
              </LazyContent>
            </div>
          )}

          {isFirstPage && (
            <Grid container={true} className={classes.relatedCategories}>
              <Grid item={true} lg={9} md={12}>
                {!!related_categories?.length && (
                  <div data-testid="related-categories">
                    <EventContext
                      value={{ engagementComponent: 'related-categories' }}
                    >
                      <h4 className={classes.relatedCategoriesTitle}>
                        Related categories
                      </h4>
                      <Grid container={true} spacing={2}>
                        {related_categories.map((categoryItem) => (
                          <Grid
                            key={categoryItem.slug}
                            item={true}
                            md={4}
                            sm={6}
                            xs={12}
                          >
                            <Category.MiniCard
                              category={categoryItem}
                              {...(isNoFollowRelatedCategories
                                ? { rel: 'nofollow' }
                                : {})}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </EventContext>
                  </div>
                )}
              </Grid>
            </Grid>
          )}
        </BuyersGuideProvider>
      </Container>
    </div>
  );
};

export default Serp;
